import { useEffect, useState } from 'react';
import { listSubmissions } from '../lib/api';
import type { PublicSubmission } from '../shared/types';

export function Wall() {
  const [items, setItems] = useState<PublicSubmission[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    listSubmissions()
      .then((res) => {
        if (cancelled) return;
        setItems(res.submissions);
        setCursor(res.nextCursor);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the wall right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function loadMore() {
    if (!cursor) return;
    setLoading(true);
    try {
      const res = await listSubmissions(cursor);
      setItems((prev) => [...prev, ...res.submissions]);
      setCursor(res.nextCursor);
    } catch {
      setError('Could not load more.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="section" id="wall" aria-labelledby="wall-heading">
      <div className="section__inner">
        <h2 id="wall-heading" className="section__title">
          People who did it
        </h2>

        {error && <p className="notice notice--error">{error}</p>}

        {!error && !loading && items.length === 0 && (
          <p className="notice">Nobody yet. Be the first.</p>
        )}

        {items.length > 0 && (
          <ul className="wall">
            {items.map((item) => (
              <li key={item.id} className="card">
                {item.photoUrl && (
                  <a
                    className="card__photo-link"
                    href={item.photoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="card__photo"
                      // Prefer the ~600px thumbnail; older records without one
                      // fall back to the full-size photo. srcset lets a retina
                      // card pull the full image only when it truly needs the
                      // pixels, while the small card keeps the light thumbnail.
                      src={item.thumbnailUrl ?? item.photoUrl}
                      {...(item.thumbnailUrl
                        ? {
                            srcSet: `${item.thumbnailUrl} 600w, ${item.photoUrl} 1200w`,
                            sizes: '(min-width: 640px) 320px, 90vw',
                          }
                        : {})}
                      alt={`${item.name} deleting their DoorDash account`}
                      loading="lazy"
                    />
                  </a>
                )}
                <div className="card__body">
                  <p className="card__name">{item.name}</p>
                  {item.note && <p className="card__note">{item.note}</p>}
                  <time className="card__date" dateTime={item.createdAt}>
                    {new Date(item.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </time>
                </div>
              </li>
            ))}
          </ul>
        )}

        {loading && <p className="notice">Loading…</p>}

        {cursor && !loading && (
          <button type="button" className="btn btn--ghost" onClick={loadMore}>
            Load more
          </button>
        )}
      </div>
    </section>
  );
}
